import {logout, getUser, isRiaUser} from '@simpleinvest/front-end-auth';
import windowHelper from '@client/util/windowHelper';
// eslint-disable-next-line import/no-cycle
import store from '@client/store';
import {ErrorRouteNames} from '@client/routes/errors';
import {Category, Logger} from '@simpleinvest/front-end-logger';
import {Route, NavigationGuardNext} from 'vue-router';
import {isQueryParamValueTrue, getQueryParamValue} from '@client/routes/helpers';
import {ClientWindow} from '@client/custom-window';

declare let window: ClientWindow;

const logger = new Logger({
  category: Category.Routing,
  fileName: 'login.js',
});

export enum LoginRouteNames {
  AdvLoginPage = 'AdvLogin',
  LoginPage = 'Login',
  LogoutPage = 'Logout',
  ForgotPasswordPage = 'ForgotPassword',
}

const redirectUrlList = new Set([process.env.VUE_APP_BASE_FOLDER]);

export const redirectToAppIfLoggedIn = async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
  try {
    await getUser();

    const isAdvisor = await isRiaUser();

    if (isAdvisor) {
      windowHelper.location.assign(`${window.BASE_URL}/advisor`);
    } else {
      windowHelper.location.assign(`${window.BASE_URL}/clientdashboard`);
    }

    // eslint-disable-next-line callback-return
    next();
  } catch {
    if (
      to.name === LoginRouteNames.LoginPage &&
      store.state.settings.enterprise.enterpriseId &&
      store.state.settings.enterprise.features.includes('SSO')
    ) {
      if (store.state.settings.enterprise.sso) {
        const redirectUrl = process.env.VUE_APP_BASE_FOLDER;

        if (redirectUrlList.has(redirectUrl)) {
          const redirectPath = store.state.redirectPath ? `/${[...redirectUrlList][0]}${store.state.redirectPath}` : '';

          windowHelper.location.assign(`${store.state.settings.enterprise.sso.loginUrl}${redirectPath}`);
        }
      } else {
        logger.logException({error: new Error(`Enterprise SSO settings not set. Unable to redirect to IdP's login url.`)});

        // eslint-disable-next-line callback-return
        next({name: ErrorRouteNames.SystemErrorPage});
      }
    } else {
      // eslint-disable-next-line callback-return
      next();
    }
  }
};

interface LoginPageQueryParams {
  loginApproved: boolean;
  emailVerified: boolean;
  inactive: boolean;
  email: string;
  routeName: string;
}

export const getLoginPageQueryParams = (route: Route, routeName: string): LoginPageQueryParams => {
  let email = null;
  let loginApproved = null;
  let emailVerified = null;
  let inactive = null;

  if (route.query.email) {
    const emailQueryValue = getQueryParamValue(route.query.email);

    email = route.query.email.includes('@') ? route.query.email : atob(decodeURIComponent(emailQueryValue));
  }

  // eslint-disable-next-line no-undefined
  if (route.query.loginApproved !== undefined) {
    loginApproved = isQueryParamValueTrue(route.query.loginApproved);
  }

  // eslint-disable-next-line no-undefined
  if (route.query.emailVerified !== undefined) {
    emailVerified = isQueryParamValueTrue(route.query.emailVerified);
  }

  // eslint-disable-next-line no-undefined
  if (route.query.inactive !== undefined) {
    inactive = isQueryParamValueTrue(route.query.inactive);
  }

  return {
    loginApproved,
    emailVerified,
    inactive,
    email,
    routeName,
  };
};

export default [
  {
    path: '/advisorlogin',
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    component: () => import('@client/layouts/login/Index.vue'),
    meta: {
      useLayouts: true,
    },
    children: [
      {
        path: '',
        name: LoginRouteNames.AdvLoginPage,
        meta: {
          isLoginRoute: true,
          publicRoute: true,
          useLayouts: true,
        },
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/pages/LoginPage.vue'),
        props: (route: Route): LoginPageQueryParams => getLoginPageQueryParams(route, LoginRouteNames.AdvLoginPage),
        beforeEnter: redirectToAppIfLoggedIn,
      },
    ],
  },
  {
    path: '/',
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    component: () => import('@client/layouts/login/Index.vue'),
    meta: {
      useLayouts: true,
    },
    children: [
      {
        path: '',
        name: LoginRouteNames.LoginPage,
        meta: {
          isLoginRoute: true,
          publicRoute: true,
          useLayouts: true,
        },
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/pages/LoginPage.vue'),
        props: (route: Route): LoginPageQueryParams => getLoginPageQueryParams(route, LoginRouteNames.LoginPage),
        beforeEnter: redirectToAppIfLoggedIn,
      },
    ],
  },
  {
    path: '/clientdashboard/logout',
    name: LoginRouteNames.LogoutPage,
    meta: {
      publicRoute: true,
    },
    beforeEnter: async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
      // TODO: consolidate all logout info here.
      await logout();

      next({name: 'Login', query: to.query});
    },
  },
  {
    path: '/forgot-password',
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    component: () => import('@client/layouts/login/Index.vue'),
    meta: {
      useLayouts: true,
    },
    children: [
      {
        path: '',
        name: LoginRouteNames.ForgotPasswordPage,
        meta: {
          isLoginRoute: true,
          publicRoute: true,
          useLayouts: true,
        },
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        component: () => import('@client/pages/ForgotPasswordPage.vue'),
        props: (route: Route): LoginPageQueryParams => getLoginPageQueryParams(route, LoginRouteNames.ForgotPasswordPage),
        beforeEnter: redirectToAppIfLoggedIn,
      },
    ],
  },
];
